import { Directive, inject, output, signal } from '@angular/core';
import { Observable, finalize } from 'rxjs';
import { ContactFormErrors, ContactFormStore, validateContact } from './contact-form.store';
import { SignupAddressStore } from '../address/signup-address.store';
import { ContactSubmission } from '../intake/signup-submit.service';

/**
 * Shared validate → submit → emit flow for the contact forms. Subclasses choose whether phone is
 * required and which endpoint the saved responses go to.
 */
@Directive()
export abstract class ContactFormBase {
  protected readonly addressStore = inject(SignupAddressStore);

  protected readonly contact = inject(ContactFormStore).contact;
  protected readonly errors = signal<ContactFormErrors>({});
  protected readonly submitting = signal(false);
  protected readonly submitError = signal('');
  /** Set once a submission succeeds; the form submits only once. */
  protected readonly done = signal(false);

  /** Emits once the submission succeeds. */
  readonly submitted = output<void>();

  protected abstract readonly phoneRequired: boolean;
  protected abstract send(submission: ContactSubmission): Observable<unknown>;

  onSubmit(event: Event): void {
    event.preventDefault();
    if (this.submitting() || this.done()) return;

    const errors = validateContact(this.contact(), { phoneRequired: this.phoneRequired });
    this.errors.set(errors);
    if (Object.keys(errors).length) return;

    this.submitting.set(true);
    this.submitError.set('');
    this.send(this.submission())
      .pipe(finalize(() => this.submitting.set(false)))
      .subscribe({
        next: () => {
          this.done.set(true);
          this.submitted.emit();
        },
        error: () => this.submitError.set('Something went wrong. Please try again.'),
      });
  }

  /** The responses as they stand, for the address on file. */
  protected submission(): ContactSubmission {
    return {
      ...this.contact(),
      address: this.addressStore.address(),
    };
  }
}
